/**
 * /payroll-l2/timesheet-feed — Live ingest queue
 * Shows the most recent labor events from the Bynaus timesheet app,
 * filterable by status. Rows can be overridden or rejected inline.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import type { LaborEvent, LaborEventVerificationStatus } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/timesheet-feed")({
  component: TimesheetFeedPage,
});

const STATUS_COLOR: Record<LaborEventVerificationStatus, "default" | "success" | "warning" | "error" | "info"> = {
  pending: "default",
  verified: "success",
  flagged: "warning",
  overridden: "info",
  rejected: "error",
};

const TABS: { value: LaborEventVerificationStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "verified", label: "Verified" },
  { value: "flagged", label: "Flagged" },
  { value: "overridden", label: "Overridden" },
  { value: "rejected", label: "Rejected" },
];

function fmtDt(s: string | null) {
  if (!s) return "—";
  return new Date(s).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function TimesheetFeedPage() {
  const [tab, setTab] = useState<LaborEventVerificationStatus | "all">("all");
  const [overrideTarget, setOverrideTarget] = useState<LaborEvent | null>(null);
  const [rejectTarget, setRejectTarget] = useState<LaborEvent | null>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const queryKey = ["payroll-l2", "labor-events", tab];
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: "100" });
      if (tab !== "all") params.set("status", tab);
      return await api<LaborEvent[]>(`/api/payroll-l2/labor-events?${params}`);
    },
  });

  const overrideMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return await api<LaborEvent>(`/api/payroll-l2/labor-events/${id}/override`, {
        method: "POST",
        body: { reason },
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payroll-l2"] }); setOverrideTarget(null); },
  });

  const rejectMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      return await api<LaborEvent>(`/api/payroll-l2/labor-events/${id}/reject`, {
        method: "POST",
        body: { reason },
      });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["payroll-l2"] }); setRejectTarget(null); },
  });

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Timesheet Feed</Typography>
        <HelpHint docPath="payroll-l2/timesheet-feed.md" label="Timesheet feed help" />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
      </Tabs>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load labor events.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Clock In</TableCell>
              <TableCell>Clock Out</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((ev) => (
              <TableRow
                key={ev.id}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate({ to: "/payroll-l2/labor-event/$eventId", params: { eventId: ev.id } })}
              >
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{ev.employee_id.slice(0, 8)}</TableCell>
                <TableCell>{fmtDt(ev.clock_in)}</TableCell>
                <TableCell>{fmtDt(ev.clock_out)}</TableCell>
                <TableCell>{ev.worked_hours ?? "—"}</TableCell>
                <TableCell>
                  <Chip label={ev.verification_status} color={STATUS_COLOR[ev.verification_status]} size="small" />
                </TableCell>
                <TableCell>
                  {ev.flag_reasons?.map((f) => (
                    <Chip key={f} label={f} size="small" color="warning" variant="outlined" sx={{ mr: 0.5, fontSize: 10 }} />
                  ))}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {ev.verification_status === "flagged" && (
                    <>
                      <Button size="small" onClick={() => setOverrideTarget(ev)}>Override</Button>
                      <Button size="small" color="error" onClick={() => setRejectTarget(ev)}>Reject</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Override dialog */}
      <ReasonDialog
        title="Override Labor Event"
        open={!!overrideTarget}
        onClose={() => setOverrideTarget(null)}
        onSubmit={(reason) => overrideMut.mutate({ id: overrideTarget!.id, reason })}
        loading={overrideMut.isPending}
      />

      {/* Reject dialog */}
      <ReasonDialog
        title="Reject Labor Event"
        open={!!rejectTarget}
        onClose={() => setRejectTarget(null)}
        onSubmit={(reason) => rejectMut.mutate({ id: rejectTarget!.id, reason })}
        loading={rejectMut.isPending}
      />
    </Box>
  );
}

function ReasonDialog({ title, open, onClose, onSubmit, loading }: {
  title: string;
  open: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  loading: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!reason.trim() || loading}
          onClick={() => onSubmit(reason.trim())}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}
